/**
 * Leviathan Web - Error Handler Utility
 * Quasar Prime LLC
 *
 * Standardized error handling for API responses and form submissions
 */

import { extractFieldErrors, extractMessage } from './formErrors';

/**
 * API error response structure from the Leviathan API
 */
export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    stack?: string;
  };
  timestamp: string;
}

/**
 * Extracts a user-friendly error message from various error types
 *
 * NAMES THE FIELD. This used to return the API's top-level
 * `error.message`, which for any validation failure is the literal string
 * "Validation failed" — the message a fuel distributor evaluating the demo
 * instance was given when ship-to creation failed, with no way to tell which
 * of thirteen fields was at fault. `details.issues[]` carried a path and a
 * message per field the whole time and was discarded here.
 *
 * Delegating the API-shaped cases to `extractMessage` upgrades all ~39 call
 * sites of `parseApiError`/`getErrorMessage` at once. Forms that additionally
 * want the message rendered AT the field use `readApiFailure` directly, which
 * also returns `fieldErrors`.
 */
export function getErrorMessage(error: unknown): string {
  console.error('Error occurred:', error);

  // Handle fetch Response object
  if (error instanceof Response) {
    return `Request failed with status ${error.status}`;
  }

  // API-shaped body: let the shared extractor name the offending fields.
  if (
    typeof error === 'object' &&
    error !== null &&
    !(error instanceof Error) &&
    Object.keys(extractFieldErrors(error)).length > 0
  ) {
    return extractMessage(error, 'Request failed');
  }

  // Handle Error objects
  if (error instanceof Error) {
    // Network errors
    if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
      return 'Unable to connect to server. Please check if the API is running.';
    }
    return error.message;
  }

  // Handle API error response objects
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;

    // Leviathan API error format: { success: false, error: { message: '...' } }
    if (errorObj.error && typeof errorObj.error === 'object') {
      const apiError = errorObj.error as Record<string, unknown>;
      if (typeof apiError.message === 'string') {
        return apiError.message;
      }
      // If error.error is still an object, stringify it
      return JSON.stringify(apiError);
    }

    // Simple { message: '...' } format
    if (typeof errorObj.message === 'string') {
      return errorObj.message;
    }

    // Fallback: stringify the object
    try {
      return JSON.stringify(error);
    } catch {
      return 'An unexpected error occurred';
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Parses error from a fetch response
 */
export async function parseApiError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    return getErrorMessage(data);
  } catch {
    // Response body wasn't JSON
    return `Request failed with status ${response.status}: ${response.statusText}`;
  }
}

/**
 * Helper function for form error handling
 */
export function handleFormError(error: unknown, setError: (msg: string) => void): void {
  const message = getErrorMessage(error);
  setError(message);
}

/**
 * Creates a standardized error from a fetch response
 */
export async function createApiError(response: Response): Promise<Error> {
  const message = await parseApiError(response);
  return new Error(message);
}
